import { FormEvent, useEffect, useState } from 'react';
import { clientApi, clientErrorMessage } from '../lib/api';
import { getStoredClientName, setStoredClientName } from '../lib/clientName';
import { getStoredReceiptEmail, setStoredReceiptEmail } from '../lib/receiptEmail';
import { roundNumber } from '../../shared/progress';
import { deadlineLine } from '../../shared/deadline';
import type { ScriptStatus, SuggestionStatus } from '../../shared/types';
import Button from './Button';
import ApprovalCeremony from './ApprovalCeremony';
import { showToast } from './Toaster';

/** Which kind of document is under review. Only the words change. */
export type ReviewKind = 'script' | 'brief';

interface Props {
  slug: string;
  /** 'brief' on an interview brief, so client-facing copy says "brief". */
  kind: ReviewKind;
  scriptStatus: ScriptStatus;
  /** 2A: rounds past three keep status v3; this says "Round 4". */
  publishedRound?: number | null;
  approveBy: string | null;
  approvedAt: number | null;
  suggestionStatus: SuggestionStatus | null;
  /** True until the initial GET of the active suggestion resolves. Gates the
   *  Suggest-edits action so a fast click can't race ahead of that fetch —
   *  before it resolves we don't yet know if there's a submitted draft
   *  in-flight, and starting fresh in that window can drop the client into
   *  an editable view of a draft that's actually already been sent. */
  suggestionLoading: boolean;
  feedbackSubmittedAt: number | null;
  feedbackSubmittedBy: string;
  commentsEnabled: boolean;
  suggestionsEnabled: boolean;
  /** Called to enter Suggest mode. Only ever wired up while the script isn't
   *  approved — the card collapses to the read-only view below once it is,
   *  so this is never reachable post-approval (closes the bug where the old
   *  header kept a live Suggest edits button after approval). */
  onEnterSuggestMode: () => void | Promise<void>;
  /** Scrolls/focuses the comments column and surfaces the "how to comment" hint. */
  onCommentClick: () => void;
  /** Reveals + focuses the general notes panel further down the page. */
  onLeaveGeneralNote: () => void;
  /** Called after Send for review succeeds; parent should refresh suggestion + script state. */
  onAfterSubmit?: () => void | Promise<void>;
  /** Submits an editing suggestion through its lock/version-checked endpoint. */
  onBeforeSubmit?: (authorName: string, note: string) => void | Promise<void>;
  /** Called after client-side approval succeeds; parent should refresh script state. */
  onAfterApprove?: () => void | Promise<void>;
  /** Script title + client name — passed to the approval ceremony for the
   *  certificate and the confirmation copy. */
  title: string;
  clientName: string;
  /** The client's dashboard slug, for the "track on your dashboard" pointer. */
  dashboardSlug: string | null;
}

function todayLocalIso(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

const GRID_COLS: Record<number, string> = { 1: 'grid-cols-1', 2: 'grid-cols-2', 3: 'grid-cols-3' };

/**
 * The single "Your review" card — full-width, between the hero and the
 * script. Absorbs what used to be ClientStatusCard's finish-line CTAs
 * (Send for review / Send another update / Approve script) plus the three
 * numbered review actions that used to live in the header (Comment /
 * Suggest edits / General notes). Content composed left-to-right and
 * centred rather than the old top-to-bottom card; nothing here appears
 * anywhere else on the page. Collapses to a single centred line once the
 * script is approved — see the mock-up's §06 for the reasoning.
 */
export default function ClientReviewCard({
  slug,
  kind,
  scriptStatus,
  publishedRound,
  approveBy,
  approvedAt,
  suggestionStatus,
  suggestionLoading,
  feedbackSubmittedAt,
  feedbackSubmittedBy,
  commentsEnabled,
  suggestionsEnabled,
  onEnterSuggestMode,
  onCommentClick,
  onLeaveGeneralNote,
  onAfterSubmit,
  onBeforeSubmit,
  onAfterApprove,
  title,
  clientName,
  dashboardSlug,
}: Props) {
  const [submittedAt, setSubmittedAt] = useState<number | null>(feedbackSubmittedAt);
  const [submittedBy, setSubmittedBy] = useState<string>(feedbackSubmittedBy);
  const [showModal, setShowModal] = useState(false);
  const [showCeremony, setShowCeremony] = useState(false);

  useEffect(() => {
    setSubmittedAt(feedbackSubmittedAt);
    setSubmittedBy(feedbackSubmittedBy);
  }, [feedbackSubmittedAt, feedbackSubmittedBy]);

  async function handleSubmit(authorName: string, note: string, email: string) {
    if (onBeforeSubmit) await onBeforeSubmit(authorName, note);
    const result = await clientApi.lockFeedback(slug, authorName, note, email);
    setSubmittedAt(result.feedback_submitted_at);
    setSubmittedBy(authorName);
    setShowModal(false);
    showToast('success', 'Sent for review. Henry has been notified.');
    if (onAfterSubmit) await onAfterSubmit();
  }

  // The one word that changes between a script and an interview brief, used
  // everywhere the card names the thing the client is reviewing.
  const noun = kind === 'brief' ? 'brief' : 'script';

  const isApproved = scriptStatus === 'approved';
  const hasEditingDraft = suggestionStatus === 'editing';
  const hasSubmittedDraft = suggestionStatus === 'submitted';
  const everSent = submittedAt !== null;
  // Client-side approve is only available from v2 or v3 — clients shouldn't
  // be approving a draft they haven't seen revisions on yet.
  const canApprove = !isApproved && (scriptStatus === 'v2' || scriptStatus === 'v3');

  // The approved-collapsed card and the modals below are NOT mutually
  // exclusive: the approval ceremony (showCeremony) opens while the script
  // is still v2/v3, then its own onApproved fires the parent's refetch,
  // which flips scriptStatus to 'approved' — isApproved goes true and the
  // card body below switches to the collapsed view WHILE the ceremony
  // modal (confetti, confirmation, certificate download) is still open on
  // top of it. Returning early here would unmount the ceremony mid-
  // celebration the instant the refetch lands, so cardBody is computed
  // conditionally but the function always returns through the single tail
  // that also renders showModal/showCeremony.
  const cardBody = isApproved ? (
    <div data-print-hide className="max-w-7xl mx-auto px-6 md:px-12 mt-4 md:mt-6">
      <div className="relative bg-card border border-accent rounded-md px-5 md:px-8 py-4">
        <div className="flex items-center justify-center gap-3 flex-wrap text-left">
          <span
            aria-hidden
            className="shrink-0 w-8 h-8 rounded-full bg-accent/10 text-accent flex items-center justify-center"
          >
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 6 9 17l-5-5" />
            </svg>
          </span>
          <p className="text-[14px] text-muted leading-[1.45] min-w-0">
            <b className="text-fg font-semibold">
              {approvedAt ? `Approved on ${formatDate(approvedAt)}.` : 'Approved.'}
            </b>{' '}
            Sheahan Studios is now moving this {noun} into production.
            {dashboardSlug && (
              <>
                {' '}
                <a
                  href={`https://dashboard.sheahanstudios.com/${dashboardSlug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-accent font-semibold whitespace-nowrap hover:opacity-80 transition-opacity"
                >
                  Track what happens next →
                </a>
              </>
            )}
          </p>
        </div>
      </div>
    </div>
  ) : (
    (() => {
      const round = scriptStatus === 'v3' && publishedRound && publishedRound > 3
        ? publishedRound
        : roundNumber(scriptStatus);
      const deadline = deadlineLine(scriptStatus, approveBy, todayLocalIso());

      // The three numbered actions — only the ones the script actually has
      // enabled render, and numbering is sequential over whatever's left, so
      // "General notes" is never stuck reading "3" when it's really the only
      // other action on offer.
      const actions: Array<{
        key: string;
        label: string;
        desc: string;
        onClick: () => void;
        gold?: boolean;
        disabled?: boolean;
        title?: string;
      }> = [];
      if (commentsEnabled) {
        actions.push({
          key: 'comment',
          label: 'Comment',
          desc: 'Highlight any line to leave a note.',
          onClick: onCommentClick,
        });
      }
      if (suggestionsEnabled) {
        const label = hasSubmittedDraft ? 'Continue changes' : hasEditingDraft ? 'Resume editing' : 'Suggest edits';
        const desc = hasSubmittedDraft
          ? 'Your edits are ready to send.'
          : hasEditingDraft
          ? 'Pick up where your team left off.'
          : 'Rewrite lines directly: it autosaves.';
        actions.push({
          key: 'suggest',
          // Idle label while the suggestion fetch is still in flight — we
          // don't yet know if there's a draft, so don't claim "Resume"/
          // "Continue" only to relabel a moment later.
          label: suggestionLoading ? 'Suggest edits' : label,
          desc: suggestionLoading ? 'Checking for an in-progress draft…' : desc,
          onClick: () => onEnterSuggestMode(),
          gold: !suggestionLoading && (hasEditingDraft || hasSubmittedDraft),
          disabled: suggestionLoading,
          title: suggestionLoading ? 'Checking for an in-progress draft…' : undefined,
        });
      }
      actions.push({
        key: 'notes',
        label: 'General notes',
        desc: 'Big-picture thoughts, not line-specific.',
        onClick: onLeaveGeneralNote,
      });

      // Finish line: one primary action normally, or Approve (primary)
      // alongside a demoted secondary once canApprove — same demotion rule
      // the old status card used, just building a shorter button list since
      // the numbered actions above now own Suggest edits / Resume editing /
      // Continue changes.
      const baseFinish: { label: string; variant: 'primary' | 'secondary' | 'ghost'; onClick: () => void } = everSent
        ? { label: 'Send another update', variant: 'ghost', onClick: () => setShowModal(true) }
        : { label: 'Send for review', variant: 'primary', onClick: () => setShowModal(true) };
      const finishButtons = canApprove
        ? [
            {
              label: noun === 'brief' ? 'Approve brief' : 'Approve script',
              variant: 'primary' as const,
              onClick: () => setShowCeremony(true),
            },
            baseFinish.variant === 'primary' ? { ...baseFinish, variant: 'secondary' as const } : baseFinish,
          ]
        : [baseFinish];

      const approvalNote =
        scriptStatus === 'draft' || scriptStatus === 'v1'
          ? 'Approval unlocks after the next revision round.'
          : null;

      return (
        <div data-print-hide className="max-w-7xl mx-auto px-6 md:px-12 mt-4 md:mt-6">
          <div className="relative bg-card border border-accent rounded-md px-5 md:px-10 py-4 animate-rise-subtle">
            {/* Status line — round + deadline framing. Divider only on mobile,
                where the actions row below drops the connecting arrows and
                needs a visual break instead. */}
            <div className="text-center text-[13px] md:text-[14px] text-muted leading-snug pb-3 md:pb-2 border-b border-line md:border-b-0">
              <span className="font-semibold text-accent">Round {round}</span>
              {' · '}
              {everSent ? (
                <>
                  Sent for review by{' '}
                  <b className="text-fg font-semibold">{submittedBy || 'your team'}</b>
                  {submittedAt ? ` · ${formatRelative(submittedAt)}` : ''}
                </>
              ) : deadline ? (
                deadline.kind === 'overdue' ? (
                  deadline.text
                ) : (
                  <>
                    {deadline.verb} by{' '}
                    <b className="text-accent font-semibold">{deadline.date}</b> to keep the project
                    schedule on track.
                  </>
                )
              ) : (
                'Reply when you’re ready.'
              )}
            </div>

            {/* The numbered actions, left to right. Quiet arrows between them
                on desktop only (medium gray, decorative); mobile drops the
                arrows and the one-line descriptions to keep three items from
                wrapping at phone width. */}
            <div className={`relative grid ${GRID_COLS[actions.length]} pt-2.5 md:pt-3`}>
              {actions.map((a, i) => (
                <div key={a.key} className="relative">
                  {i > 0 && (
                    <span
                      aria-hidden
                      className="hidden md:flex absolute left-0 top-[26px] -translate-x-1/2 -translate-y-1/2 text-faint text-[15px] leading-none pointer-events-none"
                    >
                      →
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={a.onClick}
                    disabled={a.disabled}
                    title={a.title}
                    className="
                      w-full flex flex-col items-center text-center gap-0.5
                      px-2 md:px-4 py-2.5 md:py-3 rounded-md
                      transition-colors duration-150 hover:bg-elevated
                      disabled:opacity-45 disabled:pointer-events-none disabled:hover:bg-transparent
                    "
                  >
                    <span
                      className={`inline-flex items-center justify-center w-5 h-5 rounded-full font-mono text-[10px] font-bold text-page ${
                        a.gold ? 'bg-gold' : 'bg-accent'
                      }`}
                    >
                      {i + 1}
                    </span>
                    <span className="text-[13px] md:text-[15px] font-semibold text-fg">{a.label}</span>
                    <span className="hidden md:block text-[12px] text-faint leading-snug whitespace-nowrap">
                      {a.desc}
                    </span>
                  </button>
                </div>
              ))}
            </div>

            <div aria-hidden className="hidden md:flex items-center justify-center text-faint text-[15px] leading-none py-1">
              ↓
            </div>

            <div className="flex items-center justify-center gap-3 flex-wrap pt-2.5 md:pt-0 border-t border-line md:border-t-0">
              {finishButtons.map((b, i) => (
                <Button
                  key={i}
                  variant={b.variant}
                  glow={b.variant === 'primary'}
                  onClick={b.onClick}
                  className="min-w-[184px]"
                >
                  {b.label}
                </Button>
              ))}
            </div>
            {approvalNote && (
              <p className="text-center text-[11px] text-faint mt-2">{approvalNote}</p>
            )}
          </div>
        </div>
      );
    })()
  );

  return (
    <>
      {cardBody}

      {showModal && (
        <SendModal
          isResend={everSent}
          hasEditingDraft={hasEditingDraft}
          onCancel={() => setShowModal(false)}
          onSubmit={handleSubmit}
        />
      )}
      {showCeremony && (
        <ApprovalCeremony
          slug={slug}
          kind={kind}
          title={title}
          clientName={clientName}
          status={scriptStatus}
          dashboardSlug={dashboardSlug}
          onApproved={() => {
            if (onAfterApprove) void onAfterApprove();
          }}
          onClose={() => setShowCeremony(false)}
        />
      )}
    </>
  );
}

// ---- Send modal ----

interface ModalProps {
  isResend: boolean;
  hasEditingDraft: boolean;
  onCancel: () => void;
  onSubmit: (authorName: string, note: string, email: string) => Promise<void>;
}

function SendModal({ isResend, hasEditingDraft, onCancel, onSubmit }: ModalProps) {
  const [name, setName] = useState(() => getStoredClientName());
  const [note, setNote] = useState('');
  const [email, setEmail] = useState(() => getStoredReceiptEmail());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !busy) onCancel();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy, onCancel]);

  async function handle(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) {
      setError('Your name is required.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      setStoredClientName(name.trim());
      if (email.trim()) setStoredReceiptEmail(email.trim());
      await onSubmit(name.trim(), note.trim(), email.trim());
    } catch (err) {
      setError(clientErrorMessage(err, 'That didn’t send. Please try again in a moment.'));
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 bg-ink-900/70 backdrop-blur-md flex items-center justify-center px-6 z-50 animate-fade-in"
      onClick={busy ? undefined : onCancel}
    >
      <form
        onSubmit={handle}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-card border border-line rounded-md p-8 shadow-flare-lg/30 animate-scale-in"
      >
        <span className="font-mono text-[11px] uppercase font-medium tracking-[0.12em] text-accent">
          // {isResend ? 'Send another update' : 'Send for review'}
        </span>
        <h2 className="font-display font-bold text-[24px] leading-[1.15] tracking-[-0.01em] mt-3 mb-2">
          {isResend ? 'Notify Sheahan Studios again' : 'Send for review'}
        </h2>
        <p className="text-muted text-[14px] leading-[1.55] mb-6">
          {hasEditingDraft
            ? 'Your suggested edits will be submitted along with any comments and notes. Sheahan Studios will be notified.'
            : "Sheahan Studios will be notified that your team has finished this round."}
        </p>

        <label className="block mb-4">
          <span className="block font-mono text-[11px] uppercase tracking-[0.08em] text-muted mb-2">
            Your name
          </span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            className="
              w-full px-4 py-3 bg-elevated border border-line rounded-sm
              text-fg text-[15px]
              focus:border-accent focus:shadow-focus focus:outline-none
              transition-all duration-150
            "
          />
        </label>

        <label className="block mb-4">
          <span className="block font-mono text-[11px] uppercase tracking-[0.08em] text-muted mb-2">
            Message for Henry <span className="text-faint normal-case">(optional)</span>
          </span>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            placeholder="Anything that doesn’t fit in a comment: context, priorities, deadlines…"
            className="
              w-full px-4 py-3 bg-elevated border border-line rounded-sm
              text-fg text-[14px] resize-y
              focus:border-accent focus:shadow-focus focus:outline-none
              transition-all duration-150
            "
          />
        </label>

        <label className="block mb-4">
          <span className="block font-mono text-[11px] uppercase tracking-[0.08em] text-muted mb-2">
            Email <span className="text-faint normal-case">(optional, for a confirmation receipt)</span>
          </span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            className="
              w-full px-4 py-3 bg-elevated border border-line rounded-sm
              text-fg text-[15px]
              focus:border-accent focus:shadow-focus focus:outline-none
              transition-all duration-150
            "
          />
        </label>

        {error && <p className="text-[13px] text-accent mb-3">{error}</p>}

        <div className="flex justify-end gap-3 mt-6">
          <Button type="button" variant="ghost" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" glow disabled={busy}>
            {busy ? 'Sending…' : 'Send'}
          </Button>
        </div>
      </form>
    </div>
  );
}

function formatRelative(ts: number): string {
  const diff = Date.now() - ts;
  const m = Math.floor(diff / 60_000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m} minute${m === 1 ? '' : 's'} ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hour${h === 1 ? '' : 's'} ago`;
  return formatDate(ts);
}

function formatDate(ts: number): string {
  return new Date(ts).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}
